import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingCart, Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useCart } from "@/components/CartContext";
import { NotificationBadge } from "@/components/NotificationBadge";
import ProfileMenu from "@/components/ProfileMenu";

const PRIMARY_NAV_LINKS = [
  { path: "/", label: "Home" },
  { path: "/live-events", label: "Events" },
  { path: "/collectives", label: "Collectives" },
  { path: "/media", label: "Media" },
  { path: "/bookstore", label: "Bookstore" },
];

const MORE_NAV_LINKS = [
  { path: "/about", label: "About" },
  { path: "/rental", label: "Rental" },
  { path: "/links", label: "Links" },
  { path: "/forms", label: "Forms" },
];

const ALL_NAV_LINKS = [...PRIMARY_NAV_LINKS, ...MORE_NAV_LINKS];
const LOGO = "https://assets.wordaliveglobal.org/branding/wagc_logo_small.png";

export default function DesktopNav() {
  const location = useLocation();
  const { cartCount } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className={`bg-navy text-white sticky top-0 z-50 shadow-lg transition-all duration-300 ${isScrolled ? 'h-16' : 'md:h-24 h-16'}`}>
      <div className="container-page">
        <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? 'h-16' : 'md:h-24 h-16'}`}>
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO} alt="WAGC Logo" className={`transition-all duration-300 ${isScrolled ? 'h-10' : 'md:h-14 h-10'}`} />
            <span className={`font-heading hidden md:block tracking-wide transition-all duration-300 ${isScrolled ? 'text-base' : 'md:text-xl text-base'}`}>
              Word Alive Global Center
            </span>
            <span className="font-heading hidden sm:block md:hidden tracking-wide text-base">WAGC</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
            {PRIMARY_NAV_LINKS.map((link) => (
              <Link key={link.path} to={link.path} className={`text-xs uppercase tracking-widest font-bold transition-colors hover:text-gold ${location.pathname === link.path ? "text-gold" : "text-white/80"}`}>
                {link.label}
              </Link>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="more-menu-trigger" className={`flex items-center gap-1 text-xs uppercase tracking-widest font-bold transition-colors hover:text-gold focus:outline-none ${MORE_NAV_LINKS.some(l => location.pathname === l.path) ? "text-gold" : "text-white/80"}`}>
                  More <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-navy border-gold/20 min-w-[140px]">
                {MORE_NAV_LINKS.map((link) => (
                  <DropdownMenuItem key={link.path} asChild className="cursor-pointer">
                    <Link to={link.path} className={`w-full text-xs uppercase tracking-wider font-bold ${location.pathname === link.path ? "text-gold" : "text-white/80 hover:text-white"}`}>
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="flex items-center gap-3 sm:gap-4">
            <NotificationBadge isSignedIn={!!localStorage.getItem("auth_token")} />
            <Link to="/checkout" className="relative" data-testid="cart-icon">
              <ShoppingCart className="h-5 w-5 text-gold" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-navy text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </Link>
            <ProfileMenu />
            <Link to="/giving">
              <Button data-testid="header-give-btn" className="bg-gold text-navy hover:bg-gold/90 px-5 h-9 font-bold uppercase tracking-wider text-xs hidden md:inline-flex rounded-sm">Give</Button>
            </Link>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-white hover:text-gold" data-testid="mobile-menu-btn">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-navy text-white border-gold/20 w-64">
                <div className="flex flex-col gap-1 mt-6">
                  {ALL_NAV_LINKS.map((link) => (
                    <Link key={link.path} to={link.path} onClick={() => setMobileOpen(false)} className={`text-base uppercase tracking-wider py-3 px-2 transition-colors hover:text-gold border-b border-white/5 font-heading ${location.pathname === link.path ? "text-gold" : ""}`}>
                      {link.label}
                    </Link>
                  ))}
                  <Link to="/giving" onClick={() => setMobileOpen(false)} className="text-base uppercase tracking-wider py-3 px-2 text-gold font-heading font-semibold">Give</Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
